import type { CompletionItem, CorpusValue } from '../types';

const valueLabel = (value: CorpusValue): string => value.name?.trim() || value.item_key;

/** Build a short, report-specific starter set for the contextual chat drawer.
 * The first prompt gives orientation; the next prompts turn concrete corpus
 * gaps into actions; the final prompt deliberately looks beyond the export. */
export const buildReportChatSuggestions = (values: CorpusValue[]): string[] => {
  const suggestions = ['סכם את הממצאים המרכזיים, הסיכונים והחריגים בדוח'];
  const review = values.find((value) => value.status === 'flagged' || value.status === 'needs_review');
  const missing = values.find((value) => value.status === 'missing');

  if (review) {
    suggestions.push(`אמת מול המקור את ״${valueLabel(review)}״ והסבר את החריגה`);
  }
  if (missing) {
    suggestions.push(`נסה להשלים מהדוח את ״${valueLabel(missing)}״ וציין עמוד וציטוט`);
  }

  suggestions.push('אילו קובננטים, ביאורים או סיכונים מהותיים מופיעים בדוח ולא באקסל?');
  return Array.from(new Set(suggestions)).slice(0, 4);
};

const HEBREW_STOP_WORDS = new Set([
  'אשר', 'את', 'או', 'של', 'כפי', 'לפי', 'מתוך', 'בדוח', 'בדוחות', 'הדוח',
  'החברה', 'חברה', 'נתון', 'נתונים', 'שנתי', 'שנתית', 'לתקופה', 'תקופה',
]);

const normalizeForMatch = (value: string): string => value
  .normalize('NFKC')
  .toLocaleLowerCase('he')
  .replace(/[‐‑‒–—−]/g, '-')
  .replace(/[^\p{L}\p{N}%-]+/gu, ' ')
  .replace(/\s+/g, ' ')
  .trim();

const meaningfulHebrewTokens = (value: string): string[] => (
  normalizeForMatch(value)
    .split(' ')
    .filter((token) => /^[\u0590-\u05ff]+$/u.test(token))
    .filter((token) => token.length >= 3 && !HEBREW_STOP_WORDS.has(token))
);

const latinTokens = (value: string): string[] => (
  normalizeForMatch(value)
    .split(' ')
    .filter((token) => /^[a-z][a-z0-9%-]{1,}$/i.test(token))
);

const completionItemMatchScore = (
  normalizedContent: string,
  contentTokens: Set<string>,
  item: CompletionItem,
): number => {
  const name = item.name?.trim();
  if (!name) return 0;

  const normalizedName = normalizeForMatch(name);
  if (!normalizedName) return 0;
  if (normalizedContent.includes(normalizedName)) return 100 + Math.min(normalizedName.length, 30);

  const acronyms = latinTokens(name).filter((token) => token.length >= 3);
  const acronymMatches = acronyms.filter((token) => contentTokens.has(token));
  if (acronymMatches.length > 0) return 85 + acronymMatches.length;

  const hebrew = meaningfulHebrewTokens(name);
  const hebrewMatches = hebrew.filter((token) => contentTokens.has(token));
  if (hebrew.length >= 2 && hebrewMatches.length === hebrew.length) return 75 + hebrew.length;
  if (hebrew.length >= 3 && hebrewMatches.length >= 2 && hebrewMatches.length / hebrew.length >= 0.66) {
    return 65 + hebrewMatches.length;
  }
  if (hebrew.length === 1 && hebrew[0].length >= 5 && hebrewMatches.length === 1) return 55;
  return 0;
};

/**
 * Treat the chat answer as a routing hint only: identify which open completion
 * items it discusses. The caller still sends every match through the grounded
 * targeted-extraction pipeline before the completion case is changed.
 */
export const matchCompletionItemsInChat = (
  content: string,
  items: CompletionItem[],
  limit = 6,
): CompletionItem[] => {
  if (limit <= 0) return [];
  const normalizedContent = normalizeForMatch(content);
  if (!normalizedContent) return [];
  const contentTokens = new Set(normalizedContent.split(' '));

  return items
    .filter((item) => (
      item.applicability !== 'not_applicable'
      && !['verified', 'calculated', 'not_applicable'].includes(item.evidence_status)
      && item.resolution_status !== 'resolved'
    ))
    .map((item) => ({ item, score: completionItemMatchScore(normalizedContent, contentTokens, item) }))
    .filter(({ score }) => score > 0)
    .sort((left, right) => (
      right.score - left.score
      || left.item.item_key.localeCompare(right.item.item_key, 'en', { numeric: true })
    ))
    .slice(0, limit)
    .map(({ item }) => item);
};
